import { useState, useEffect, useRef } from 'react'
import { useAuth } from './useAuth'
import { checkUserInDatabase } from '../lib/apiPhpAdapter'

/**
 * Stejná kontrola přístupu jako na hlavní stránce intranetu (zaměstnanecký účet / doména).
 */
export function useIntranetAccess() {
  const { user, loading: authLoading } = useAuth()
  const [accessGranted, setAccessGranted] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const retryAttemptedRef = useRef(false)

  useEffect(() => {
    retryAttemptedRef.current = false
  }, [user?.id, user?.email])

  useEffect(() => {
    if (authLoading) {
      return
    }

    let cancelled = false

    const checkAccess = async () => {
      if (!user) {
        if (!cancelled) {
          setAccessGranted(false)
          setLoading(false)
        }
        return
      }

      try {
        const userData = await checkUserInDatabase() as any

        if (cancelled) return

        if (!userData) {
          console.error('No user data returned from checkUserInDatabase')
          if (!cancelled) {
            setAccessGranted(false)
            setLoading(false)
          }
          return
        }

        if (!userData.database) {
          console.warn('User not found in database, but authenticated via OAuth:', userData.token)
          if (userData.token?.email) {
            const email = userData.token.email.toLowerCase()
            const isEmployeeDomain = email.endsWith('@iss-cheb.cz')
            const isStudentDomain = email.endsWith('@student.iss-cheb.cz')

            if (isEmployeeDomain && !isStudentDomain) {
              console.log('✅ Access granted: user authenticated via OAuth with employee domain')
              setAccessGranted(true)
              if (!cancelled) {
                setLoading(false)
              }
              return
            }
          }

          console.error('User not found in database and no valid employee domain')
          if (!cancelled) {
            setAccessGranted(false)
            setLoading(false)
          }
          return
        }

        const dbUser = userData.database
        const tokenUser = userData.token

        const isEmployee = dbUser.user_type === 'employee'
        const isEmployeeOrNull = isEmployee || dbUser.user_type === null || dbUser.user_type === undefined
        const isAdmin = dbUser.role === 'admin'
        const emailMatches = tokenUser?.email === dbUser.email

        const email = tokenUser?.email?.toLowerCase() || dbUser.email?.toLowerCase() || ''
        const hasEmployeeEmail = email.endsWith('@iss-cheb.cz')
        const hasStudentEmail = email.endsWith('@student.iss-cheb.cz')

        const canAccessAsEmployee = isEmployeeOrNull && (hasEmployeeEmail && !hasStudentEmail)

        if (cancelled) return

        if (isAdmin && emailMatches) {
          console.log('✅ Access granted: user is admin with matching email')
          setAccessGranted(true)
        } else if (canAccessAsEmployee && emailMatches) {
          console.log('✅ Access granted: user is employee (or NULL user_type with employee email) with matching email')
          setAccessGranted(true)
        } else if (hasEmployeeEmail && !hasStudentEmail) {
          console.log('✅ Access granted: employee email domain detected')
          setAccessGranted(true)
        } else {
          console.warn('⚠️ Access check inconclusive, keeping loading state')
        }
      } catch (error) {
        console.error('Error checking access:', error)
        if (!cancelled && !retryAttemptedRef.current) {
          console.log('Retrying access check after error (user might have been just created)...')
          retryAttemptedRef.current = true
          setTimeout(async () => {
            if (cancelled) return
            try {
              const retryUserData = await checkUserInDatabase() as any
              if (cancelled) return

              if (!retryUserData) {
                if (!cancelled) {
                  setAccessGranted(false)
                  setLoading(false)
                }
                return
              }

              if (!retryUserData.database) {
                if (retryUserData.token?.email) {
                  const email = retryUserData.token.email.toLowerCase()
                  const isEmployeeDomain = email.endsWith('@iss-cheb.cz')
                  const isStudentDomain = email.endsWith('@student.iss-cheb.cz')

                  if (isEmployeeDomain && !isStudentDomain) {
                    console.log('✅ Access granted on retry: user authenticated via OAuth with employee domain')
                    if (!cancelled) {
                      setAccessGranted(true)
                      setLoading(false)
                    }
                    return
                  }
                }

                if (!cancelled) {
                  setAccessGranted(false)
                  setLoading(false)
                }
                return
              }

              const retryDbUser = retryUserData.database
              const retryTokenUser = retryUserData.token

              const retryIsEmployee = retryDbUser.user_type === 'employee'
              const retryIsEmployeeOrNull =
                retryIsEmployee || retryDbUser.user_type === null || retryDbUser.user_type === undefined
              const retryIsAdmin = retryDbUser.role === 'admin'
              const retryEmailMatches = retryTokenUser?.email === retryDbUser.email
              const retryIdMatches = retryUserData.match === true

              const retryEmail = retryTokenUser?.email?.toLowerCase() || retryDbUser.email?.toLowerCase() || ''
              const retryHasEmployeeEmail = retryEmail.endsWith('@iss-cheb.cz')
              const retryHasStudentEmail = retryEmail.endsWith('@student.iss-cheb.cz')

              const retryCanAccessAsEmployee =
                retryIsEmployeeOrNull && (retryHasEmployeeEmail && !retryHasStudentEmail)

              if (cancelled) return

              if (retryIsAdmin && retryEmailMatches) {
                console.log('✅ Access granted on retry: user is admin')
                setAccessGranted(true)
              } else if (retryCanAccessAsEmployee && retryEmailMatches && retryIdMatches) {
                console.log('✅ Access granted on retry: user is employee')
                setAccessGranted(true)
              } else {
                console.error('❌ Access denied on retry')
                setAccessGranted(false)
              }
            } catch (retryError) {
              console.error('Error on retry:', retryError)
              if (!cancelled) {
                setAccessGranted(false)
              }
            } finally {
              if (!cancelled) {
                setLoading(false)
              }
            }
          }, 1500)
          return
        }
        if (!cancelled) {
          setAccessGranted(false)
          setLoading(false)
        }
      } finally {
        if (!cancelled && !retryAttemptedRef.current) {
          setLoading(false)
        }
      }
    }

    const timeoutId = setTimeout(() => {
      checkAccess()
    }, 100)

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [user, authLoading])

  useEffect(() => {
    if (accessGranted === null && user?.email) {
      const email = user.email.toLowerCase()
      if (email.endsWith('@iss-cheb.cz') && !email.endsWith('@student.iss-cheb.cz')) {
        setAccessGranted(true)
        setLoading(false)
      }
    }
  }, [accessGranted, user])

  return { user, authLoading, accessGranted, loading }
}
